import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { Alert, AutoComplete, Button, Checkbox, Form, Input } from 'antd';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import styled from '@emotion/styled';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import dayjs from 'dayjs';

import SectionMessage from 'components/SectionMessage/SectionMessage';

import useAgent from 'hooks/useAgent';
import useAnalytics from 'hooks/useAnalytics';
import useEditFormStatuses from 'hooks/useEditFormStatuses';
import useTimeoutValue from 'hooks/useTimeoutValue';
import { UPDATE_CUSTOMER_ADDRESS } from 'mutations/customerMeta';
import { ADDRESS_SUGGESTIONS } from 'queries/address';
import { CUSTOMER_OVERVIEW } from 'queries/customer';

import { graphQLErrorContainsCode } from 'utilities/helpers';
import {
  ANALYTICS_EVENTS,
  BREAKPOINT,
  ERROR_CODES,
  SIZE,
  STATES_ABBR,
  STATUS,
} from 'utilities/constants';
import LimitedAccessCheckbox from 'components/LimitedAccessCheckbox/LimitedAccessCheckbox';

const AddressGrid = styled.div`
  @media (min-width: ${BREAKPOINT.SM_PX}) {
    display: grid;
    grid-gap: 0 var(--spacing);
    grid-template-areas:
      'street1 street1'
      'street2 street2'
      'city    state'
      '. postalCode';
  }

  @media (min-width: ${BREAKPOINT.LG_PX}) {
    display: grid;
    grid-gap: 0 var(--spacing);
    grid-template-areas:
      'street1 street1 street1 street1 street2 street2 street2    street2'
      'city    city    state   state   state   state   postalCode postalCode';
  }
`;

const FormItem = styled(Form.Item)`
  grid-area: ${(props) => props.name};
`;

const ADDRESS = Object.freeze({
  street1: 'street1',
  street2: 'street2',
  city: 'city',
  state: 'state',
  postalCode: 'postalCode',
  forceUpdate: 'forceUpdate',
});

const STATE_OPTIONS = Object.keys(STATES_ABBR).map((key) => {
  return { value: STATES_ABBR[key].name };
});

export const getStateName = (stateAbbr) => {
  return STATES_ABBR[stateAbbr]?.name || '';
};

export const formatSuggestion = (addressInfo) => {
  const { city, secondary, state, street, zip } = addressInfo;
  const street2 = secondary ? secondary + ', ' : '';
  return `${street}, ${street2}${city}, ${state}, ${zip}`;
};

const EditCustomerAddressForm = ({ onSuccess }) => {
  const formName = 'customerAddress';

  const agent = useAgent();
  const { customerId } = useParams();
  const { trackEvent } = useAnalytics();

  const [form] = Form.useForm();

  const cityRef = useRef();
  const postalCodeRef = useRef();
  const stateRef = useRef();
  const street2Ref = useRef();

  const [submissionError, setSubmissionError] = useState('');
  const [submissionWarning, setSubmissionWarning] = useState('');
  const [submissionSuccess, setSubmissionSuccess] = useTimeoutValue(
    false,
    3000,
  );

  const {
    containsChanges,
    getFieldStatus,
    initialValues,
    setInitialValues,
    updateFieldStatus,
  } = useEditFormStatuses(form, () => {
    setSubmissionWarning('');
  });

  // CUSTOMER
  const [getCustomerMeta, customerMetaQuery] = useLazyQuery(CUSTOMER_OVERVIEW, {
    fetchPolicy: 'network-only',
  });

  const [customer, customerTooNew] = useMemo(() => {
    let customer;
    let customerTooNew = false;

    if (customerMetaQuery.data) {
      customer = customerMetaQuery.data.customer;

      const accounts = customer?.accounts || [];
      const sortedAccounts = [...accounts].sort(
        (a1, a2) => Date.parse(a1.createdAt) > Date.parse(a2.createdAt),
      );
      const createdAt = sortedAccounts[0]?.createdAt;
      if (createdAt) {
        const threshold = dayjs(createdAt).add(90, 'day');
        customerTooNew = dayjs().isBefore(threshold);
      }
    }

    return [customer, customerTooNew];
  }, [customerMetaQuery.data]);

  // ADDRESS SUGGESTIONS
  const [getAddressSuggestions, addressSuggestionsQuery] =
    useLazyQuery(ADDRESS_SUGGESTIONS);

  const addressSuggestions = useMemo(() => {
    const suggestions = addressSuggestionsQuery.data?.findAddress || [];
    return suggestions.map((suggestion, index) => {
      return {
        label: formatSuggestion(suggestion),
        value: `${index}`,
        suggestion,
      };
    });
  }, [addressSuggestionsQuery.data]);

  const [updateAddress, updateAddressMutation] = useMutation(
    UPDATE_CUSTOMER_ADDRESS,
    {
      refetchQueries: ['GetCustomerOverview'],
      onCompleted: () => {
        setSubmissionSuccess(true);
        getCustomerMeta({ variables: { customerId } });
        onSuccess && onSuccess();
      },
      onError: (error) => {
        const addressNotRecognized = graphQLErrorContainsCode(
          error,
          ERROR_CODES.INVALID_ADDR_NO_MATCHES,
        );

        const addressInvalidSpelling = graphQLErrorContainsCode(
          error,
          ERROR_CODES.INVALID_ADDR_SPELLING,
        );

        let errorMessage = error.message;
        if (addressNotRecognized) {
          errorMessage =
            'Address not recognized. Please consult escalations for a force update.';
        } else if (addressInvalidSpelling) {
          errorMessage =
            'Please double check spelling before consulting escalations for a force update.';
        }

        setSubmissionError(errorMessage);
      },
    },
  );

  const handleStreetChange = useCallback(
    _debounce((value) => {
      if (value && value.length >= 3) {
        getAddressSuggestions({
          variables: {
            address: value,
          },
        });
      }
    }, 300),
    [],
  );

  const fillAddressFields = (value, option) => {
    const { suggestion } = option;
    if (suggestion) {
      const { city, secondary, state, street, zip } = suggestion;
      form.setFieldsValue({
        [ADDRESS.city]: city,
        [ADDRESS.postalCode]: zip,
        [ADDRESS.state]: getStateName(state),
        [ADDRESS.street1]: street,
        [ADDRESS.street2]: secondary,
      });

      // Awkward, but need to force field status check on anything that may have an updated value
      setTimeout(() => {
        street2Ref.current?.focus();
        cityRef.current?.focus();
        stateRef.current?.focus();
        postalCodeRef.current?.focus();
        postalCodeRef.current?.blur();
      }, 200);
    }
  };

  const resetFormValues = () => {
    const address = _get(customer, 'contactInfo.address', {});
    setInitialValues({
      [ADDRESS.city]: address.city,
      [ADDRESS.postalCode]: address.postalCode?.slice(0, 5) || '',
      [ADDRESS.state]: getStateName(address.state),
      [ADDRESS.street1]: address.street1,
      [ADDRESS.street2]: address.street2,
      [ADDRESS.forceUpdate]: false,
    });
  };

  const handleSubmit = async () => {
    setSubmissionError('');
    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      return;
    }

    if (!containsChanges) {
      setSubmissionWarning(
        "You haven't made any updates to the customers address.",
      );
      return;
    }

    const stateAbbr = Object.keys(STATES_ABBR).find((key) => {
      return STATES_ABBR[key].name === values.state;
    });

    trackEvent(ANALYTICS_EVENTS.ADDRESS_EDIT_SAVED);

    updateAddress({
      variables: {
        customerId: customer.id,
        street1: values.street1,
        street2: values.street2,
        city: values.city,
        state: stateAbbr,
        postalCode: values.postalCode,
        overrideAddressValidation: form.getFieldValue(ADDRESS.forceUpdate),
      },
    });
  };

  useEffect(() => {
    resetFormValues();
  }, [customer]);

  useEffect(() => {
    if (customerId) {
      getCustomerMeta({ variables: { customerId } });
    }
  }, [customerId]);

  const formDisabled =
    updateAddressMutation.loading || customerMetaQuery.loading;
  const street2ForbiddenValues = ['same', 'n/a', 'none'];

  return (
    <Form
      form={form}
      name={formName}
      layout="vertical"
      initialValues={initialValues}
      onFieldsChange={updateFieldStatus}
      onFinish={handleSubmit}
      style={{ position: 'relative' }}
    >
      {customerTooNew ? (
        <Alert
          banner
          showIcon={true}
          type="warning"
          title="Caller has been a customer less than 90 days, supporting documents are required before proceeding with updating the address"
          style={{ marginBottom: 'var(--spacing)' }}
        />
      ) : null}
      <AddressGrid>
        <SectionMessage
          data-testid="address-success-message"
          status={STATUS.SUCCESS}
          size={SIZE.SM}
          text="Address Updated"
          cover={true}
          visible={submissionSuccess}
        ></SectionMessage>
        <FormItem
          label="Street 1"
          name={ADDRESS.street1}
          rules={[{ required: true, message: 'Street 1 is required' }]}
        >
          <AutoComplete
            data-testid="street1-input"
            disabled={formDisabled}
            onChange={handleStreetChange}
            onSelect={fillAddressFields}
            options={addressSuggestions}
            loading={addressSuggestionsQuery.loading}
            status={getFieldStatus(ADDRESS.street1)}
          />
        </FormItem>
        <FormItem
          label="Street 2"
          extra="(Apt/Suite #)"
          name={ADDRESS.street2}
          rules={[
            () => ({
              validator(_, value) {
                if (street2ForbiddenValues.includes(value.toLowerCase())) {
                  return Promise.reject(new Error('That value is not allowed'));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input
            placeholder="Leave blank if not applicable"
            data-testid="street2-input"
            disabled={formDisabled}
            ref={street2Ref}
            status={getFieldStatus(ADDRESS.street2)}
          />
        </FormItem>
        <FormItem
          label="City"
          name={ADDRESS.city}
          rules={[{ required: true, message: 'City is required' }]}
        >
          <Input
            data-testid="city-input"
            disabled={formDisabled}
            ref={cityRef}
            status={getFieldStatus(ADDRESS.city)}
          />
        </FormItem>
        <FormItem
          label="State"
          name={ADDRESS.state}
          rules={[{ required: true, message: 'State is required' }]}
        >
          <AutoComplete
            data-testid="state-input"
            disabled={formDisabled}
            ref={stateRef}
            options={STATE_OPTIONS}
            status={getFieldStatus(ADDRESS.state)}
            filterOption={(inputValue, option) => {
              return (
                option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !==
                -1
              );
            }}
          />
        </FormItem>
        <FormItem
          label="Postal Code"
          name={ADDRESS.postalCode}
          rules={[
            {
              message: 'Postal code should be at least 5 characters long',
              min: 5,
              required: true,
            },
          ]}
        >
          <Input
            data-testid="postal-code-input"
            disabled={formDisabled}
            maxLength={5}
            ref={postalCodeRef}
            status={getFieldStatus(ADDRESS.postalCode)}
          />
        </FormItem>
      </AddressGrid>

      {submissionWarning ? (
        <Alert
          data-testid="address-submission-warning"
          banner
          type="warning"
          title={submissionWarning}
          style={{ marginBottom: 'var(--spacing)' }}
        ></Alert>
      ) : null}

      {submissionError ? (
        <Alert
          data-testid="address-failure-message"
          banner
          type="error"
          title={submissionError}
          style={{ marginBottom: 'var(--spacing)' }}
        ></Alert>
      ) : null}

      <LimitedAccessCheckbox
        name={ADDRESS.forceUpdate}
        data-testid="force-update-checkbox"
        disabled={formDisabled}
        hasAccess={agent.isSuperUser}
      >
        Force address update. Once you have verified with the customer and{' '}
        <a
          href="https://tools.usps.com/zip-code-lookup.htm?byaddress"
          target="_blank"
          rel="noreferrer"
        >
          USPS
        </a>{' '}
        that it exists you may check this box to force an update.
      </LimitedAccessCheckbox>

      <Button type="primary" htmlType="submit">
        Update Address
      </Button>
    </Form>
  );
};

export default EditCustomerAddressForm;
